import { styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BotelBookingForm from '../../components/BookBotelForm';
import BotelCard from '../../components/BotelCard';
import BotelCardSkeleton from '../../components/BotelCardSkeleton';
import FilterButtons from '../../components/FilterButtons';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { DURATIONS_PRICES } from '../../constants/booking';
import { useNotification } from '../../context/NotificationContext';
import { Botel, botelService } from '../../services/api/botelService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PageContainer = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(0,0,0,0.7)'
});

const HeroSection = styled('div')({
  position: 'relative',
  background: 'background.default',
  backgroundImage: 'url(images/filter-background.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const BotelSection = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'background.default',
  backgroundImage: 'url(images/background.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  padding: '32px 0',
});

const BotelContainer = styled('div')({
  width: '100%',
  maxWidth: 1200,
  display: 'flex',
  flexDirection: 'column',
  padding: '0 16px',
});

const BotelGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '32px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr 1fr 1fr',
  },
}));

const EmptyState = styled('div')({
  textAlign: 'center',
  padding: '48px 16px',
  color: '#fff',
  '& h3': {
    fontSize: '24px',
    marginBottom: '16px',
  },
  '& p': {
    fontSize: '16px',
    opacity: 0.8,
  },
});

const FilteredBotels = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const location = query.get('location') || '';
  const date = query.get('dateTime') || '';
  const duration = query.get('duration') || '';

  const [botels, setBotels] = useState<Botel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const botelPrice = useMemo(() => DURATIONS_PRICES[duration as keyof typeof DURATIONS_PRICES], [duration]);

  const handleBotelClick = (botel: Botel) => {
    // Store botel data in session storage
    sessionStorage.setItem('selectedBotel', JSON.stringify({
      id: botel.id,
      name: botel.name,
      price: botelPrice,
      location: botel.location_address,
      image: botel.images?.[0] || '/images/botelOne.png',
      checkIn: date,
      duration: duration
    }));
    navigate('/booking-details');
  };

  useEffect(() => {
    const fetchBotels = async () => {
      if (!location) {
        setBotels([]);
        setLoading(false);
        showNotification('Please select a location to search for botels', 'info');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await botelService.searchBotels(location, date, duration);
        setBotels(response.botels);
        if (response.botels.length === 0) {
          showNotification('No botels found matching your search criteria', 'warning');
        }
      } catch (err) {
        const errorMessage = 'Failed to fetch botels. Please try again later.';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchBotels();
  }, [location, date, duration, showNotification]);

  const renderBotelCards = () => {
    if (loading) {
      return Array(6).fill(null).map((_, index) => (
        <BotelCardSkeleton key={index} />
      ));
    }

    if (error) {
      return (
        <EmptyState>
          <h3>Error</h3>
          <p>{error}</p>
        </EmptyState>
      );
    }

    if (botels.length === 0) {
      return (
        <EmptyState>
          <h3>No Botels Found</h3>
          <p>We couldn't find any botels matching your search criteria. Try adjusting your filters or search in a different location.</p>
        </EmptyState>
      );
    }

    return botels.map((botel, index) => (
      <BotelCard
        key={botel.id}
        index={index}
        title={botel.name}
        price={`$${botelPrice}/${duration}`}
        distance={botel.location_address}
        security={`Device Status: ${botel.device_status}`}
        imageUrl={botel.images?.[0] || '/images/botelOne.png'}
        onClick={() => handleBotelClick(botel)}
      />
    ));
  };

  return (
    <PageContainer>
      <HeroSection>
        <Header />
        <BotelBookingForm />
      </HeroSection>

      <BotelSection>
        <BotelContainer>
          <FilterButtons />
          <BotelGrid>
            {renderBotelCards()}
          </BotelGrid>
        </BotelContainer>
      </BotelSection>
      <Footer />
    </PageContainer>
  );
};

export default FilteredBotels;